import { ApolloClient } from 'apollo-client'
import { createUploadLink } from 'apollo-upload-client'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ApolloLink, Observable } from 'apollo-link'
import store from '../store'
import router from '../router'
import { onError } from "apollo-link-error";

const RATE_LIMIT_MESSAGE = 'Demasiadas peticiones, intenta más tarde.';

function getRateLimitMessage(networkError) {
    if (networkError?.result && typeof networkError.result === 'object' && networkError.result.error) {
        return networkError.result.error;
    }
    return RATE_LIMIT_MESSAGE;
}

function isRateLimitError(message) {
    return message && (String(message).includes('429') || String(message).includes('Demasiadas peticiones'));
}

let isRefreshing = false;
let isLoggingOut = false;
let pendingRequests = [];

const resolvePendingRequests = () => {
    pendingRequests.forEach(cb => cb());
    pendingRequests = [];
};

const doLogout = () => {
    if (!isLoggingOut) {
        isLoggingOut = true;
        pendingRequests = [];
        store.dispatch('logout');
        router.push({ name: 'login' });
        setTimeout(() => { isLoggingOut = false; }, 5000);
    }
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward, response }) => {
    if (graphQLErrors) {
        const hasUnauthenticated = graphQLErrors.some(e => e.extensions?.code === 'UNAUTHENTICATED');
        if (hasUnauthenticated) {
            const opName = operation?.operationName;
            if (opName !== 'auth' && opName !== 'refreshToken') {
                // Si el retry también falla con UNAUTHENTICATED → no volver a refrescar, logout
                if (operation.getContext().retried) {
                    doLogout();
                    return;
                }

                if (!store.getters.refreshTokenIsExpired()) {
                    // Access token expirado pero refresh token válido — renovar silenciosamente
                    if (!isRefreshing) {
                        isRefreshing = true;
                        return new Observable(observer => {
                            store.dispatch('renewToken')
                                .then(newToken => {
                                    if (!newToken) {
                                        isRefreshing = false;
                                        doLogout();
                                        observer.error(new Error('Token refresh returned null'));
                                        return;
                                    }
                                    store.commit('setAccessToken', newToken);
                                    isRefreshing = false;
                                    resolvePendingRequests();
                                    // Marcar operación como ya reintentada y forzar nuevo token en header
                                    // (doble garantía: authLink también lo leerá del store)
                                    const existingHeaders = operation.getContext().headers || {};
                                    operation.setContext({
                                        retried: true,
                                        headers: {
                                            ...existingHeaders,
                                            Authorization: 'Bearer ' + newToken,
                                        },
                                    });
                                    forward(operation).subscribe({
                                        next: observer.next.bind(observer),
                                        error: observer.error.bind(observer),
                                        complete: observer.complete.bind(observer),
                                    });
                                })
                                .catch(() => {
                                    isRefreshing = false;
                                    doLogout();
                                    observer.error(new Error('Token refresh failed'));
                                });
                        });
                    } else {
                        // Renovación ya en curso — encolar este request
                        return new Observable(observer => {
                            pendingRequests.push(() => {
                                operation.setContext({ retried: true });
                                forward(operation).subscribe({
                                    next: observer.next.bind(observer),
                                    error: observer.error.bind(observer),
                                    complete: observer.complete.bind(observer),
                                });
                            });
                        });
                    }
                } else {
                    // Refresh token también expirado — logout
                    doLogout();
                }
                return;
            }
        }
    }

    // 429 rate limit: logout, ir a login y convertir a error GraphQL con mensaje claro
    if (networkError && networkError.statusCode === 429) {
        const opName = operation?.operationName;
        if (opName !== 'auth' && opName !== 'refreshToken') {
            doLogout();
        }
        const message = getRateLimitMessage(networkError);
        return Observable.of({
            errors: [{ message, extensions: { code: 'CUSTOM_ERROR' } }]
        });
    }

    // Si la respuesta ya trae errores con mensaje de rate limit: logout, ir a login y normalizar mensaje
    if (response && response.errors && response.errors.length > 0) {
        const hasRateLimitError = response.errors.some((e) => isRateLimitError(e.message));
        if (hasRateLimitError) {
            const opName = operation?.operationName;
            if (opName !== 'auth' && opName !== 'refreshToken') {
                doLogout();
            }
        }
        const cleanedErrors = response.errors.map((e) => {
            if (isRateLimitError(e.message)) {
                return { ...e, message: RATE_LIMIT_MESSAGE };
            }
            return e;
        });
        if (cleanedErrors.some((e, i) => e.message !== response.errors[i].message)) {
            return Observable.of({ ...response, errors: cleanedErrors });
        }
    }

    if (networkError) {
        console.error(`[Network error]: ${networkError}`);
    }

    return forward(operation);
});

const uploadLink = createUploadLink({
    uri: (process.env.VUE_APP_APIHOST ? process.env.VUE_APP_APIHOST : '') + '/graphql/',
})

//Middleware for Authorization
const authLink = new ApolloLink((operation, forward) => {
    const token = store.getters.getToken;
    if (token) {
        const existingHeaders = operation.getContext().headers || {};
        operation.setContext({
            headers: {
                ...existingHeaders,
                Authorization: 'Bearer ' + token
            }
        });
    }
    return forward(operation);
})


const link = ApolloLink.from([
    errorLink,
    authLink,
    uploadLink
]);

// Cache implementation
const cache = new InMemoryCache({
    addTypename: false
})

// Create the apollo client
const apolloClient = new ApolloClient({
    link: link,
    cache,
})


export default apolloClient